/**
 * Aba Métricas da Tela 7 (T7-5) — conversão verbatim dos 6 `ca-metric-card` do
 * mockup `design-references/brief-3/centro-alertas.app.jsx:571-678`, consumindo o
 * `ConflictAlertMetricsVM` (agregado real de ConflictAlertMetricsDailyRaw[]).
 *
 * Degradações honestas vs mockup (sem fonte no backend — padrão Item 317):
 *   - Totais: usa os campos reais do VM (Ativos/Mitigados/Aceitos/Falsos positivos/
 *     Resolvidos/Revogados) — não porta o "Auto-resolved" do protótipo.
 *   - Tempo médio: só value+unit; sem `target`/`ok` (✓/⚠) — backend não dá SLA.
 *   - Reincidência: `rate` real; os pills de contagem de chain mostram '—'.
 *   - Severity over time: diário (título "últimos N dias", não "12 semanas").
 *   - Top operadores: placeholder honesto quando topOperators === null (Item 321).
 *
 * CSS (.ca-metric-*) já existe no DS; este componente não cria estilos.
 */
import { ConflictAlertSeverityBadge } from '../../components';

import type { ConflictAlertMetricsVM, ConflictSeverityVM } from '../../view-models';
import type React from 'react';

export interface MetricsTabProps {
  metrics: ConflictAlertMetricsVM;
}

export function MetricsTab({ metrics }: MetricsTabProps): React.JSX.Element {
  return (
    <div className="ca-metrics">
      <div className="ca-metric-card">
        <h3>Totais · {metrics.period}</h3>
        <div className="ca-metric-totals">
          <div className="ca-metric-tot">
            <span className="ca-metric-tot__num" style={{ color: 'var(--danger-fg)' }}>
              {metrics.totals.active}
            </span>
            <span className="ca-metric-tot__label">Ativos</span>
          </div>
          <div className="ca-metric-tot">
            <span className="ca-metric-tot__num" style={{ color: 'var(--success-fg)' }}>
              {metrics.totals.mitigated}
            </span>
            <span className="ca-metric-tot__label">Mitigados</span>
          </div>
          <div className="ca-metric-tot">
            <span className="ca-metric-tot__num" style={{ color: 'var(--info-fg)' }}>
              {metrics.totals.accepted}
            </span>
            <span className="ca-metric-tot__label">Aceitos</span>
          </div>
          <div className="ca-metric-tot">
            <span className="ca-metric-tot__num">{metrics.totals.falsePositive}</span>
            <span className="ca-metric-tot__label">Falsos positivos</span>
          </div>
          <div className="ca-metric-tot">
            <span className="ca-metric-tot__num">{metrics.totals.resolved}</span>
            <span className="ca-metric-tot__label">Resolvidos</span>
          </div>
          <div className="ca-metric-tot">
            <span className="ca-metric-tot__num">{metrics.totals.revoked}</span>
            <span className="ca-metric-tot__label">Revogados</span>
          </div>
        </div>
      </div>

      <div className="ca-metric-card">
        <h3>Tempo médio de mitigação</h3>
        <div className="ca-metric-sla">
          {(
            Object.entries(metrics.avgMitigationTime) as [
              ConflictSeverityVM,
              { value: number; unit: string },
            ][]
          ).map(([sev, m]) => (
            <div key={sev} className="ca-metric-sla__row">
              <ConflictAlertSeverityBadge severity={sev} />
              <span className="ca-metric-sla__val">
                {m.value}
                {m.unit}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="ca-metric-card">
        <h3>Top Alert Types</h3>
        <div>
          {metrics.topTypes.map((t, i) => (
            <div key={t.id} className="ca-metric-types__row">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <span
                  style={{
                    fontSize: 'var(--text-xs)',
                    color: 'var(--fg-1)',
                    fontWeight: 'var(--fw-medium)',
                  }}
                >
                  {i + 1}. {t.label}
                </span>
                <div className="ca-metric-types__bar">
                  <div className="ca-metric-types__bar-fill" style={{ width: t.pct + '%' }} />
                </div>
              </div>
              <span
                className="mono"
                style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-2)', textAlign: 'right' }}
              >
                {t.count}
              </span>
              <span className="ca-metric-types__pct">{t.pct}%</span>
            </div>
          ))}
        </div>
      </div>

      <div className="ca-metric-card">
        <h3>Reincidência rate</h3>
        <div className="ca-metric-reinc">
          <div className="ca-metric-reinc__pill">
            <b>{metrics.reincidence.total}</b>total alerts
          </div>
          <div className="ca-metric-reinc__pill" style={{ background: 'var(--warning-bg)' }}>
            <b>{metrics.reincidence.chainGt1}</b>chain &gt; 1
          </div>
          <div className="ca-metric-reinc__pill">
            <b>{metrics.reincidence.chain2}</b>chain pos 2
          </div>
          <div className="ca-metric-reinc__pill" style={{ background: 'var(--danger-bg)' }}>
            <b>{metrics.reincidence.chain3plus}</b>chain pos 3+
          </div>
          <div
            className="ca-metric-reinc__pill"
            style={{ background: 'var(--bg-inverted)', color: 'var(--fg-on-inverted)' }}
          >
            <b>{metrics.reincidence.rate}%</b>média diária
          </div>
        </div>
      </div>

      <div className="ca-metric-card">
        <h3>Severity distribution (últimos {metrics.days} dias)</h3>
        <div className="ca-metric-stack">
          {(() => {
            // Normaliza pelo MAIOR total do período (não por um teto fixo) — senão dias
            // movimentados estouram a coluna e dias calmos viram stubs invisíveis (review T7).
            const maxTotal = Math.max(
              1,
              ...metrics.severityOverTime.map((d) => d.critical + d.high + d.medium + d.low),
            );
            return metrics.severityOverTime.map((wk, i) => {
              const { critical: crit, high, medium: med, low } = wk;
              const total = crit + high + med + low;
              const pct = (n: number): number => (n / maxTotal) * 100;
              return (
                <div
                  key={i}
                  className="ca-metric-stack__col"
                  title={`Dia ${i + 1}: C${crit} H${high} M${med} L${low} (total ${total})`}
                >
                  {crit > 0 && (
                    <div
                      className="ca-metric-stack__seg ca-metric-stack__seg--critical"
                      style={{ height: pct(crit) + '%' }}
                    />
                  )}
                  {high > 0 && (
                    <div
                      className="ca-metric-stack__seg ca-metric-stack__seg--high"
                      style={{ height: pct(high) + '%' }}
                    />
                  )}
                  {med > 0 && (
                    <div
                      className="ca-metric-stack__seg ca-metric-stack__seg--medium"
                      style={{ height: pct(med) + '%' }}
                    />
                  )}
                  {low > 0 && (
                    <div
                      className="ca-metric-stack__seg ca-metric-stack__seg--low"
                      style={{ height: pct(low) + '%' }}
                    />
                  )}
                </div>
              );
            });
          })()}
        </div>
        <div className="ca-metric-stack__legend">
          <span className="ca-metric-stack__legend-item">
            <span
              className="ca-metric-stack__legend-dot"
              style={{ background: 'var(--danger-fg)' }}
            />
            critical
          </span>
          <span className="ca-metric-stack__legend-item">
            <span
              className="ca-metric-stack__legend-dot"
              style={{ background: 'var(--warning-fg)' }}
            />
            high
          </span>
          <span className="ca-metric-stack__legend-item">
            <span
              className="ca-metric-stack__legend-dot"
              style={{ background: 'var(--info-fg)' }}
            />
            medium
          </span>
          <span className="ca-metric-stack__legend-item">
            <span className="ca-metric-stack__legend-dot" style={{ background: 'var(--fg-4)' }} />
            low
          </span>
        </div>
      </div>

      <div className="ca-metric-card">
        <h3>Top operadores envolvidos</h3>
        {metrics.topOperators === null ? (
          <div style={{ color: 'var(--fg-3)', fontSize: 'var(--text-sm)' }}>
            — métrica em implementação
          </div>
        ) : null}
      </div>
    </div>
  );
}
